import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSpinner,
  faInfoCircle,
  faUser,
  faEnvelope,
  faMapMarkerAlt,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import CreatableSelect from 'react-select/creatable';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import OrganizationService from '@frontend/AppSettings/services/OrganizationService';
import toastService from '@frontend/AppSettings/services/ToastService';
import { useOrganizationAnchor } from '../../OrganizationAnchorContext';
import { isEngineeringEnvironment } from '@frontend/AppSettings/environmentConfig';
import useCurrentEmployee from '@frontend/AppSettings/hooks/useCurrentEmployee';
import FormInput from '@frontend/Components/Forms/FormInput/FormInput';
import ConfirmationModal from '@frontend/Components/ConfirmationModal/ConfirmationModal';
import './EditStakeholderModal.css';

interface EditStakeholderModalProps {
  isOpen: boolean;
  onClose: () => void;
  stakeholderId: string;
  type: 'individual' | 'organization';
  stakeholderData: any;
  onUpdateSuccess?: () => void;
}

interface StakeholderFormData {
  ClientID?: string;
  FirstName: string;
  LastName: string;
  Company: string;
  Email: string;
  Phone: string;
  Address: string;
  Jurisdiction?: string;
  Title?: string;
  SecondaryPhoneNumber?: string;
  Entity?: string;
  Notes?: string;
}

const EditStakeholderModal: React.FC<EditStakeholderModalProps> = ({
  isOpen,
  onClose,
  stakeholderId,
  type,
  stakeholderData,
  onUpdateSuccess,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Non-null only on a REAL organization's detail view (P2 Build J) — a company-name
  // edit there renames the organization record, not just the contact's string.
  const organizationAnchor = useOrganizationAnchor();
  const [formData, setFormData] = useState<StakeholderFormData>({
    ClientID: '',
    FirstName: '',
    LastName: '',
    Company: '',
    Email: '',
    Phone: '',
    Address: '',
    Jurisdiction: '',
    Title: '',
    SecondaryPhoneNumber: '',
    Entity: '',
    Notes: '',
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isClient, setIsClient] = useState<boolean>(false);
  const [showRenameConfirm, setShowRenameConfirm] = useState<boolean>(false);
  const [orgOptions, setOrgOptions] = useState<{ value: string; label: string }[]>([]);
  const [orgsLoading, setOrgsLoading] = useState<boolean>(false);

  // Initialize form data when stakeholderData changes
  useEffect(() => {
    if (stakeholderData) {
      setFormData({
        ClientID: stakeholderData.ClientID || '',
        FirstName: stakeholderData.FirstName || '',
        LastName: stakeholderData.LastName || '',
        Company: stakeholderData.Company || '',
        Email: stakeholderData.Email || '',
        Phone: stakeholderData.Phone || '',
        Address: stakeholderData.Address || '',
        Jurisdiction: stakeholderData.Jurisdiction || '',
        Title: stakeholderData.Title || '',
        SecondaryPhoneNumber: stakeholderData.SecondaryPhoneNumber || stakeholderData.SecondaryPhone || '',
        Entity: stakeholderData.Entity || '',
        Notes: stakeholderData.Notes || '',
      });

      // The page fetches stakeholderData by ClientID first then AgentID, so the
      // presence of one ID field is the source of truth. Don't peek at unrelated
      // fields like Jurisdiction — they exist on both records and produced
      // misclassifications that silently 404'd PUT requests.
      if (stakeholderData.ClientID) {
        setIsClient(true);
      } else if (stakeholderData.AgentID) {
        setIsClient(false);
      } else {
        // Defensive fallback if neither ID came back. Treat individual
        // records with a Jurisdiction as agents (legacy heuristic).
        setIsClient(!stakeholderData.Jurisdiction);
      }
    }
  }, [stakeholderData, stakeholderId]);

  // Load distinct organizations from clients + agents so individuals can be
  // moved between existing organizations (or onto a brand new one).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const loadOrgs = async () => {
      setOrgsLoading(true);
      try {
        const [clients, agents] = await Promise.all([
          ClientService.getClients().catch(() => []),
          AgentsService.getAgents().catch(() => []),
        ]);
        const set = new Set<string>();
        (clients || []).forEach((c: any) => {
          if (c?.Company && typeof c.Company === 'string' && c.Company.trim()) {
            set.add(c.Company.trim());
          }
        });
        (agents || []).forEach((a: any) => {
          if (a?.Company && typeof a.Company === 'string' && a.Company.trim()) {
            set.add(a.Company.trim());
          }
        });
        if (!cancelled) {
          setOrgOptions(
            Array.from(set)
              .sort((a, b) => a.localeCompare(b))
              .map((name) => ({ value: name, label: name }))
          );
        }
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    };
    loadOrgs();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const selectedOrgOption = useMemo(() => {
    if (!formData.Company) return null;
    const existing = orgOptions.find((o) => o.value === formData.Company);
    return existing ?? { value: formData.Company, label: formData.Company };
  }, [formData.Company, orgOptions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  // Only permission level 1 (admin) can change IDs
  const currentEmployee = useCurrentEmployee();
  const rawPermishId = currentEmployee?.permish_id ?? null;
  const permishIdNum = typeof rawPermishId === 'string' ? parseInt(rawPermishId, 10) : rawPermishId;
  const canEditId = permishIdNum === 1;

  // The ID the client/agent record currently has in the database
  const currentClientId = String(stakeholderData?.ClientID || '');
  const newClientId = (formData.ClientID || '').trim();
  const isClientIdChanged =
    canEditId && isClient && !!currentClientId && !!newClientId && newClientId !== currentClientId;

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (type === 'individual' && (!formData.FirstName || !formData.LastName)) {
      setError('First name and last name are required.');
      return;
    }

    if (type === 'organization' && !formData.Company) {
      setError('Company name is required for organizations.');
      return;
    }

    if (!formData.Email) {
      setError('Email is required.');
      return;
    }

    if (isClient && currentClientId && !newClientId) {
      setError('Client ID must not be empty.');
      return;
    }

    // Changing the client ID cascades to matters and financial records —
    // make the user explicitly confirm before doing it.
    if (isClientIdChanged) {
      setShowRenameConfirm(true);
      return;
    }

    await performSave(false);
  };

  const performSave = async (renameId: boolean) => {
    setLoading(true);
    setError('');

    try {
      // For organization records the route uses an "org-" prefix that doesn't
      // exist in the underlying clients/agents tables; strip it for the PUT.
      let actualId = stakeholderId.startsWith('org-')
        ? stakeholderId.replace('org-', '')
        : (isClient ? (stakeholderData?.ClientID || stakeholderId) : (stakeholderData?.AgentID || stakeholderId));

      // Rename the ID first (cascades to matters and all referencing
      // records), then run the profile update against the new ID.
      if (renameId) {
        await ClientService.renameClient(String(currentClientId), newClientId);
        actualId = newClientId;
      }

      // Only send fields the user actually changed so optional schema-only
      // fields don't get blanked out on the server.
      const trimmedCompany = (formData.Company || '').trim();

      let ok = false;
      if (isClient) {
        const clientData: Record<string, string> = {
          FirstName: formData.FirstName,
          LastName: formData.LastName,
          Company: trimmedCompany,
          Email: formData.Email,
          Phone: formData.Phone || '',
          Address: formData.Address || '',
          Title: formData.Title || '',
          SecondaryPhoneNumber: formData.SecondaryPhoneNumber || '',
          Entity: formData.Entity || '',
          Notes: formData.Notes || '',
        };
        ok = await ClientService.updateClient(String(actualId), clientData);
      } else {
        const agentData: Record<string, string> = {
          FirstName: formData.FirstName,
          LastName: formData.LastName,
          Company: trimmedCompany,
          Email: formData.Email,
          Phone: formData.Phone || '',
          Address: formData.Address || '',
          Jurisdiction: formData.Jurisdiction || '',
          Title: formData.Title || '',
          SecondaryPhoneNumber: formData.SecondaryPhoneNumber || '',
          Entity: formData.Entity || '',
          Notes: formData.Notes || '',
        };
        ok = await AgentsService.updateAgent(String(actualId), agentData);
      }

      if (!ok) {
        throw new Error(
          'The server did not confirm the update. Please refresh and try again.'
        );
      }

      // P2 Build J: on a REAL organization's detail view, a changed company name renames
      // the ORGANIZATION record itself (the new truth) — the contact's company string
      // above stays the kept legacy alias and is updated alongside, never instead.
      if (
        organizationAnchor &&
        trimmedCompany &&
        trimmedCompany !== organizationAnchor.organization.name
      ) {
        await OrganizationService.updateOrganization(
          organizationAnchor.organization.id,
          trimmedCompany
        );
      }

      if (renameId) {
        // The old ID is baked into many query keys (matters, financials,
        // tasks) and matter IDs may have changed too — invalidate everything.
        await queryClient.invalidateQueries();
      } else {
        // Invalidate queries to refresh data on the orbit list and elsewhere.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['Clients', 'orbit'] }),
          queryClient.invalidateQueries({ queryKey: ['Agents', 'orbit'] }),
        ]);
      }

      toastService.success(
        renameId
          ? 'Client ID updated successfully.'
          : type === 'individual' ? 'Individual updated successfully.' : 'Organization updated successfully.'
      );

      if (onUpdateSuccess) {
        onUpdateSuccess();
      }

      handleClose();

      if (renameId) {
        // The current URL contains the old ID — move to the renamed record
        const newRouteId = stakeholderId.startsWith('org-')
          ? `org-${newClientId}`
          : newClientId;
        navigate(`/stakeholder/${type}/${encodeURIComponent(newRouteId)}`, { replace: true });
      }
    } catch (err: any) {
      console.error('Error updating stakeholder:', err);
      const message =
        err?.data?.error || err?.message || 'Failed to update stakeholder. Please try again.';
      setError(message);
      toastService.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Elevated above the edit modal overlay (z-index 10000) */}
      <div style={{ position: 'relative', zIndex: 10001 }}>
        <ConfirmationModal
          isOpen={showRenameConfirm}
          onClose={() => setShowRenameConfirm(false)}
          onConfirm={async () => {
            setShowRenameConfirm(false);
            await performSave(true);
          }}
          message={`Change client ID from "${currentClientId}" to "${newClientId}"? The IDs of all associated ${isEngineeringEnvironment ? 'projects' : 'matters'} and every related record will be updated to match.`}
          confirmLabel="Change ID"
          cancelLabel="Cancel"
        />
      </div>
    <div className="edit-stakeholder-overlay" onClick={handleClose}>
      <div className="edit-stakeholder-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="edit-stakeholder-header">
          <div className="edit-stakeholder-header-left">
            <h2 className="edit-stakeholder-title">
              Edit {type === 'individual' ? 'Individual' : 'Organization'}
            </h2>
            <p className="edit-stakeholder-subtitle">
              Update {type === 'individual' ? 'individual' : 'organization'} information
            </p>
          </div>
          <button
            type="button"
            className="edit-stakeholder-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="edit-stakeholder-content">
          {/* Name Section */}
          <div className="edit-stakeholder-section edit-stakeholder-section-blue">
            <div className="edit-stakeholder-section-header">
              <div className="edit-stakeholder-section-icon-wrapper edit-stakeholder-icon-blue">
                <FontAwesomeIcon icon={faInfoCircle} />
              </div>
              <h3 className="edit-stakeholder-section-title">
                {type === 'individual' ? "Stakeholder's Name" : 'Organization Name'}
              </h3>
            </div>
            <div className="edit-stakeholder-section-content">
              {canEditId && isClient && currentClientId && (
                <div style={{ width: '100%' }}>
                  <FormInput
                    label="Client ID"
                    name="ClientID"
                    value={formData.ClientID || ''}
                    onChange={handleInputChange}
                    placeholder="Enter client ID"
                  />
                  {isClientIdChanged && (
                    <div
                      style={{
                        marginTop: '6px',
                        fontSize: '0.8rem',
                        color: '#92400e',
                        lineHeight: 1.4,
                      }}
                    >
                      Changing the client ID will also update the IDs of all
                      associated {isEngineeringEnvironment ? 'projects' : 'matters'} and related records.
                    </div>
                  )}
                </div>
              )}
              {type === 'individual' ? (
                <>
                  <FormInput
                    label="First Name*"
                    name="FirstName"
                    value={formData.FirstName}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    required
                  />
                  <FormInput
                    label="Last Name*"
                    name="LastName"
                    value={formData.LastName}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    required
                  />
                </>
              ) : (
                <FormInput
                  label="Organization Name*"
                  name="Company"
                  value={formData.Company}
                  onChange={handleInputChange}
                  placeholder="Enter organization name"
                  required
                />
              )}
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="edit-stakeholder-section edit-stakeholder-section-purple">
            <div className="edit-stakeholder-section-header">
              <div className="edit-stakeholder-section-icon-wrapper edit-stakeholder-icon-purple">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <h3 className="edit-stakeholder-section-title">Contact Information</h3>
            </div>
            <div className="edit-stakeholder-section-content">
              <FormInput
                label="Email*"
                name="Email"
                type="email"
                value={formData.Email}
                onChange={handleInputChange}
                placeholder="Enter email address"
                required
              />
              <FormInput
                label="Phone"
                name="Phone"
                value={formData.Phone}
                onChange={handleInputChange}
                placeholder="Enter phone number"
              />
              <FormInput
                label="Secondary Phone"
                name="SecondaryPhoneNumber"
                value={formData.SecondaryPhoneNumber || ''}
                onChange={handleInputChange}
                placeholder="Enter secondary phone number"
              />
            </div>
          </div>

          {/* Address Section */}
          <div className="edit-stakeholder-section edit-stakeholder-section-green">
            <div className="edit-stakeholder-section-header">
              <div className="edit-stakeholder-section-icon-wrapper edit-stakeholder-icon-green">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </div>
              <h3 className="edit-stakeholder-section-title">Address</h3>
            </div>
            <div className="edit-stakeholder-section-content">
              <FormInput
                label="Address"
                name="Address"
                value={formData.Address}
                onChange={handleInputChange}
                placeholder="Enter address"
              />
            </div>
          </div>

          {/* Organization Section — individuals only.
              Picks an existing org or creates a new one; clearing detaches them. */}
          {type === 'individual' && (
            <div className="edit-stakeholder-section edit-stakeholder-section-blue">
              <div className="edit-stakeholder-section-header">
                <div className="edit-stakeholder-section-icon-wrapper edit-stakeholder-icon-blue">
                  <FontAwesomeIcon icon={faBuilding} />
                </div>
                <h3 className="edit-stakeholder-section-title">Organization</h3>
              </div>
              <div className="edit-stakeholder-section-content">
                <label
                  htmlFor="edit-stakeholder-company-select"
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#1a1a2e',
                  }}
                >
                  Company / Organization
                </label>
                <CreatableSelect
                  inputId="edit-stakeholder-company-select"
                  isClearable
                  isLoading={orgsLoading}
                  options={orgOptions}
                  value={selectedOrgOption}
                  onChange={(option) => {
                    setFormData((prev) => ({
                      ...prev,
                      Company: option?.value ?? '',
                    }));
                  }}
                  formatCreateLabel={(input) => `Create new organization "${input}"`}
                  placeholder={orgsLoading ? 'Loading organizations…' : 'Search or create an organization…'}
                  noOptionsMessage={() => 'No organizations yet — type to create one'}
                  classNamePrefix="edit-stakeholder-org-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: 40,
                      borderRadius: 6,
                      borderColor: '#d0d0d0',
                      boxShadow: 'none',
                      ':hover': { borderColor: '#a8a8a8' },
                    }),
                    menu: (base) => ({ ...base, zIndex: 20 }),
                  }}
                />
                <div
                  style={{
                    marginTop: '10px',
                    padding: '10px 12px',
                    background: '#eef2ff',
                    border: '1px solid #c7d2fe',
                    borderRadius: 6,
                    fontSize: '0.8rem',
                    color: '#3730a3',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    lineHeight: 1.5,
                  }}
                >
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginTop: 2 }} />
                  <span>
                    Pick an existing organization to move this person there, type a new name to
                    create one, or clear the field to detach them from any organization.
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Additional Information Section */}
          <div className="edit-stakeholder-section edit-stakeholder-section-orange">
            <div className="edit-stakeholder-section-header">
              <div className="edit-stakeholder-section-icon-wrapper edit-stakeholder-icon-orange">
                <FontAwesomeIcon icon={faUser} />
              </div>
              <h3 className="edit-stakeholder-section-title">Additional Information</h3>
            </div>
            <div className="edit-stakeholder-section-content">
              {type === 'individual' && !isClient && (
                <FormInput
                  label="Jurisdiction"
                  name="Jurisdiction"
                  value={formData.Jurisdiction || ''}
                  onChange={handleInputChange}
                  placeholder="Enter jurisdiction"
                />
              )}
              <FormInput
                label="Title"
                name="Title"
                value={formData.Title || ''}
                onChange={handleInputChange}
                placeholder="Enter title"
              />
              <FormInput
                label="Entity"
                name="Entity"
                value={formData.Entity || ''}
                onChange={handleInputChange}
                placeholder="Enter entity type"
              />
              <div style={{ width: '100%' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 500, color: '#1a1a2e' }}>
                  Notes
                </label>
                <textarea
                  name="Notes"
                  value={formData.Notes}
                  onChange={handleInputChange}
                  placeholder="Enter notes"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="edit-stakeholder-error">
              <FontAwesomeIcon icon={faInfoCircle} />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="edit-stakeholder-actions">
            <button
              type="button"
              className="edit-stakeholder-button edit-stakeholder-button-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="edit-stakeholder-button edit-stakeholder-button-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Updating...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default EditStakeholderModal;

